// Google Analytics 4 Configuration and Utilities
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// GA4 Measurement ID - Replace with your actual ID
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX';

// Lightweight queue so we don't lose events before GA loads
type QueuedEvent = { type: 'event' | 'config' | 'consent'; args: any[] };
const pendingEvents: QueuedEvent[] = [];
let gaInitialized = false;

const flushPending = () => {
  try {
    if (!gaInitialized || typeof window === 'undefined' || !window.gtag) return;
    while (pendingEvents.length) {
      const evt = pendingEvents.shift()!;
      // Map queued records to gtag calls
      if (evt.type === 'event') {
        window.gtag('event', ...(evt.args as [string, Record<string, any> | undefined]));
      } else if (evt.type === 'config') {
        window.gtag('config', ...(evt.args as [string, Record<string, any> | undefined]));
      } else if (evt.type === 'consent') {
        window.gtag('consent', ...(evt.args as [string, Record<string, any>]));
      }
    }
  } catch {}
};

// Add preconnect/dns-prefetch after consent, just-in-time
const addPreconnect = (href: string, opts?: { crossOrigin?: boolean }) => {
  try {
    const head = document.head || document.getElementsByTagName('head')[0];
    if (!head) return;
    const existing = head.querySelector(`link[rel="preconnect"][href="${href}"]`);
    if (existing) return;
    const pre = document.createElement('link');
    pre.rel = 'preconnect';
    pre.href = href;
    if (opts?.crossOrigin) pre.crossOrigin = '';
    head.appendChild(pre);
    const dns = document.createElement('link');
    dns.rel = 'dns-prefetch';
    dns.href = href;
    head.appendChild(dns);
  } catch {}
};

// Initialize Google Analytics (optionally deferred to idle/timeout)
export const initGA = (opts?: { defer?: boolean; idleTimeoutMs?: number }) => {
  if (typeof window === 'undefined' || gaInitialized) return;

  const load = () => {
    if (gaInitialized) return;
    try {
      // Preconnect only now (after consent path leads here)
      addPreconnect('https://www.googletagmanager.com', { crossOrigin: true });
      // Load Google Analytics script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      document.head.appendChild(script);

      // Initialize gtag and dataLayer
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        window.dataLayer.push(arguments);
      } as any;

      // Configure GA4
      window.gtag('js', new Date());
      window.gtag('config', GA_MEASUREMENT_ID, {
        page_title: document.title,
        page_location: window.location.href,
        send_page_view: false, // We'll handle page views manually for SPA
        anonymize_ip: true,
        allow_google_signals: false,
        ads_data_redaction: true,
        allow_ad_personalization_signals: false,
      });

      gaInitialized = true;
      flushPending();
    } catch {}
  };

  const shouldDefer = opts?.defer !== false; // default to deferred
  const timeout = Math.max(0, opts?.idleTimeoutMs ?? 3000);

  if (shouldDefer) {
    // Load on first interaction to ensure timely analytics without blocking LCP
    const onFirstInteraction = () => {
      try {
        removeListeners();
      } catch {}
      load();
    };
    const removeListeners = () => {
      window.removeEventListener('scroll', onFirstInteraction, { passive: true } as any);
      window.removeEventListener('pointerdown', onFirstInteraction as any);
      window.removeEventListener('keydown', onFirstInteraction as any);
    };
    window.addEventListener('scroll', onFirstInteraction, { passive: true } as any);
    window.addEventListener('pointerdown', onFirstInteraction as any, { once: true } as any);
    window.addEventListener('keydown', onFirstInteraction as any, { once: true } as any);

    // Also schedule for idle or timeout as a fallback
    if (typeof (window as any).requestIdleCallback === 'function') {
      (window as any).requestIdleCallback(() => {
        removeListeners();
        load();
      }, { timeout });
    } else {
      setTimeout(() => {
        removeListeners();
        load();
      }, timeout);
    }
  } else {
    load();
  }
};

// Track page views for SPA
export const trackPageView = (path: string, title?: string) => {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    window.gtag('event', 'page_view', {
      page_title: title || document.title,
      page_location: window.location.origin + path,
      page_path: path,
    });
  } else {
    pendingEvents.push({ type: 'event', args: ['page_view', { page_title: title || document.title, page_location: window.location.origin + path, page_path: path }] });
  }
};

// Track custom events
export const trackEvent = (
  eventName: string,
  parameters?: Record<string, any>
) => {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    window.gtag('event', eventName, parameters);
  } else {
    pendingEvents.push({ type: 'event', args: [eventName, parameters] });
  }
};

// Track user properties
export const setUserProperties = (properties: Record<string, any>) => {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      custom_map: properties
    });
  } else {
    pendingEvents.push({ type: 'config', args: [GA_MEASUREMENT_ID, { custom_map: properties }] });
  }
};

// Track user engagement
export const trackUserEngagement = (engagementTimeMs: number) => {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    window.gtag('event', 'user_engagement', {
      engagement_time_msec: engagementTimeMs
    });
  } else {
    pendingEvents.push({ type: 'event', args: ['user_engagement', { engagement_time_msec: engagementTimeMs }] });
  }
};

// Predefined event tracking functions for common actions
export const analytics = {
  // Authentication events
  trackSignUp: (method: string) => trackEvent('sign_up', { method }),
  trackLogin: (method: string) => trackEvent('login', { method }),
  trackLogout: () => trackEvent('logout'),

  // Meal planning events
  trackMealPlanGenerated: (planType: string, mealCount: number) => 
    trackEvent('meal_plan_generated', { plan_type: planType, meal_count: mealCount }),
  
  trackMealPlanSaved: (planType: string) => 
    trackEvent('meal_plan_saved', { plan_type: planType }),
  
  trackMealPlanShared: (planType: string) => 
    trackEvent('meal_plan_shared', { plan_type: planType }),

  // Preference events
  trackPreferencesUpdated: (category: string) => 
    trackEvent('preferences_updated', { category }),
  
  trackDietaryRestrictionsSet: (restrictions: string[]) => 
    trackEvent('dietary_restrictions_set', { restrictions }),

  // Feature usage
  trackFeatureUsed: (featureName: string) => 
    trackEvent('feature_used', { feature_name: featureName }),
  
  trackOptimizationUsed: (optimizationType: string) => 
    trackEvent('optimization_used', { optimization_type: optimizationType }),

  // Error tracking
  trackError: (errorType: string, errorMessage: string) => 
    trackEvent('error_occurred', { error_type: errorType, error_message: errorMessage }),

  // Performance tracking
  trackPageLoad: (loadTime: number) => 
    trackEvent('page_load', { load_time_ms: loadTime }),

  // User journey events
  trackOnboardingStep: (step: string, stepNumber: number) => 
    trackEvent('onboarding_step', { step, step_number: stepNumber }),
  
  trackSubscriptionEvent: (eventType: string, planType?: string) => 
    trackEvent('subscription_event', { event_type: eventType, plan_type: planType })
};

// Privacy and consent management
export const setConsent = (consent: boolean) => {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: consent ? 'granted' : 'denied'
    });
  } else {
    pendingEvents.push({ type: 'consent', args: ['update', { analytics_storage: consent ? 'granted' : 'denied' }] });
  }
};

// Disable tracking for development
export const disableTracking = () => {
  if (process.env.NODE_ENV === 'development') {
    console.log('Analytics tracking disabled in development mode');
    return true;
  }
  return false;
};

export default analytics;

// Best-effort removal of GA cookies when opting out
export const clearAnalyticsCookies = () => {
  if (typeof document === 'undefined') return;
  try {
    const cookies = document.cookie ? document.cookie.split(';') : [];
    const namesToClear: string[] = [];
    cookies.forEach((cookie) => {
      const name = cookie.trim().split('=')[0];
      if (/^_ga/.test(name) || name === '_gid' || name === '_gat') {
        namesToClear.push(name);
      }
    });
    const host = window.location.hostname;
    const parts = host.split('.');
    const domains = [host];
    // Also attempt parent domains: .example.com, .com (will no-op if invalid)
    for (let i = 1; i < parts.length - 0; i++) {
      const d = '.' + parts.slice(i).join('.');
      if (!domains.includes(d)) domains.push(d);
    }
    namesToClear.forEach((name) => {
      domains.forEach((domain) => {
        try {
          document.cookie = `${name}=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/; Domain=${domain}`;
        } catch {}
      });
      try {
        document.cookie = `${name}=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/`;
      } catch {}
    });
  } catch {}
};
